import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CandidateProfile, EmployerProfile, Permission, RecruiterProfile, User } from '../types/core';
import { usePlatform } from './PlatformContext';
import { can } from '../utils/permissions';
import { roleLabels } from '../utils/labels';
interface SignInResult {
  ok: boolean;
  error?: string;
  user?: User;
}
interface AuthContextValue {
  user?: User;
  candidateProfile?: CandidateProfile;
  employerProfile?: EmployerProfile;
  recruiterProfile?: RecruiterProfile;
  actor: {
    userId: string;
    name: string;
    roleLabel: string;
  };
  isSignedIn: boolean;
  signIn: (email: string, password: string) => SignInResult;
  signInAs: (userId: string) => void;
  signOut: () => void;
  hasPermission: (permission: Permission) => boolean;
  /** Attempted sign-ins in this session, shown on the admin security screen. */
  signInAttempts: {
    email: string;
    at: string;
    outcome: 'success' | 'failure';
    reason?: string;
  }[];
}
const AuthContext = createContext<AuthContextValue | null>(null);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const platform = usePlatform();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [signInAttempts, setSignInAttempts] = useState<AuthContextValue['signInAttempts']>([]);
  const user = platform.users.find(u => u.id === userId);
  const signIn = useCallback<AuthContextValue['signIn']>((email, password) => {
    const at = new Date().toISOString();
    const found = platform.users.find(u => u.email.toLowerCase() === email.trim().toLowerCase());
    const fail = (reason: string) => {
      setSignInAttempts(a => [{
        email,
        at,
        outcome: 'failure' as const,
        reason
      }, ...a].slice(0, 30));
      return {
        ok: false,
        error: reason
      };
    };
    if (!found) return fail('We could not sign you in. Check the email address and password and try again.');
    if (found.demoPassword !== password) {
      return fail('We could not sign you in. Check the email address and password and try again.');
    }
    if (found.status === 'suspended' || found.status === 'closed') {
      return fail('This account is not active. Contact support through the technical support form.');
    }
    setUserId(found.id);
    setSignInAttempts(a => [{
      email,
      at,
      outcome: 'success' as const
    }, ...a].slice(0, 30));
    platform.record('user.sign_in', {
      userId: found.id,
      name: found.fullLegalName,
      roleLabel: roleLabels[found.role]
    }, 'User', found.id, found.mfaEnabled ? 'Successful sign-in with multifactor authentication.' : 'Successful sign-in.');
    return {
      ok: true,
      user: found
    };
  }, [platform]);
  const signInAs = useCallback<AuthContextValue['signInAs']>(id => setUserId(id), []);
  const signOut = useCallback(() => {
    if (user) {
      platform.record('user.sign_out', {
        userId: user.id,
        name: user.fullLegalName,
        roleLabel: roleLabels[user.role]
      }, 'User', user.id, 'Signed out.');
    }
    setUserId(undefined);
  }, [platform, user]);
  const value = useMemo<AuthContextValue>(() => {
    const candidateProfile = user ? platform.candidateProfiles.find(p => p.userId === user.id) : undefined;
    const employerProfile = user ? platform.employerProfiles.find(p => p.userId === user.id) : undefined;
    const recruiterProfile = user ? platform.recruiterProfiles.find(p => p.userId === user.id) : undefined;
    return {
      user,
      candidateProfile,
      employerProfile,
      recruiterProfile,
      actor: {
        userId: user?.id ?? 'anonymous',
        name: user?.fullLegalName ?? 'Anonymous',
        roleLabel: user ? roleLabels[user.role] : 'Anonymous'
      },
      isSignedIn: Boolean(user),
      signIn,
      signInAs,
      signOut,
      hasPermission: (permission: Permission) => can(user?.role, permission),
      signInAttempts
    };
  }, [platform.candidateProfiles, platform.employerProfiles, platform.recruiterProfiles, signIn, signInAs, signInAttempts, signOut, user]);
  return <AuthContext.Provider value={value} data-id="element-560" data-mp="2n0gjhjgrqv8p">{children}</AuthContext.Provider>;
}
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}